import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './QuizEditor.css'

const emptyQuestion = () => ({ question: '', options: ['', '', '', ''], correct: 0 })

const QuizEditor = () => {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants, setTenants]               = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [questions, setQuestions]           = useState([emptyQuestion()])
  const [hasSaved, setHasSaved]             = useState(false)
  const [loading, setLoading]               = useState(false)
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState(null)
  const [success, setSuccess]               = useState(false)

  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          setTenants(data)
          if (data.length > 0) setSelectedTenant(data[0].id)
        })
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  const loadQuestions = () => {
    if (!selectedTenant) return
    setLoading(true)
    apiFetch(`/api/admin/quiz-questions/${selectedTenant}`)
      .then(r => r.json())
      .then(data => {
        const deQ = data.find(q => q.language === 'de')
        if (deQ?.questions?.length > 0) {
          setQuestions(deQ.questions.map(q => ({
            question: q.question,
            options:  q.options.length === 4 ? q.options : [...q.options, ...Array(4 - q.options.length).fill('')],
            correct:  q.correct,
          })))
          setHasSaved(true)
        } else {
          setQuestions([emptyQuestion()])
          setHasSaved(false)
        }
      })
      .catch(() => setError('Quiz-Fragen konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(loadQuestions, [selectedTenant])

  // ── Question editing ──────────────────────────────────────────────────────
  const updateQuestion = (qIdx, field, value) =>
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, [field]: value } : q))

  const updateOption = (qIdx, oIdx, value) =>
    setQuestions(prev => prev.map((q, i) =>
      i === qIdx ? { ...q, options: q.options.map((o, j) => j === oIdx ? value : o) } : q
    ))

  const addQuestion    = () => setQuestions(prev => [...prev, emptyQuestion()])
  const removeQuestion = (idx) => { if (questions.length > 1) setQuestions(prev => prev.filter((_, i) => i !== idx)) }

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    for (const [i, q] of questions.entries()) {
      if (!q.question.trim()) { setError(`Frage ${i+1}: Fragetext fehlt`); return }
      if (q.options.filter(o => o.trim()).length < 2) { setError(`Frage ${i+1}: Mindestens 2 Antwortmöglichkeiten erforderlich`); return }
    }
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const res = await apiFetch('/api/admin/quiz-questions', {
        method: 'POST',
        body: JSON.stringify({
          tenantId:    selectedTenant,
          sourceLang:  'de',
          targetLangs: ['de','en','es','it','pl','nl','da','fr','hu','hr','pt','cs','bg','et','fi','el','lv','lt','no','ro','ru','sr','sk','sl','sv','tr','uk'],
          questions:   questions.map(q => ({
            question: q.question,
            options:  q.options.filter(o => o.trim()),
            correct:  q.correct,
          })),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Quiz</h1>
          <p>Fragen und Antworten auf Deutsch bearbeiten</p>
        </div>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card quiz-tenant-card">
          <div className="form-group quiz-tenant-form-group">
            <label>Mandant</label>
            <select
              className="form-control"
              value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}
            >
              {tenants.map(t =>
                <option key={t.id} value={t.id}>{t.name}</option>
              )}
            </select>
          </div>
        </div>
      )}

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Quiz-Fragen gespeichert</div>}
      {!loading && hasSaved && !success && (
        <div className="alert alert-info">
          {questions.length} gespeicherte Frage{questions.length !== 1 ? 'n' : ''} geladen
        </div>
      )}

      {loading ? (
        <div className="loading-state"><span className="spinner" /></div>
      ) : (
        <>
          {questions.map((q, qIdx) => (
            <div key={qIdx} className="card quiz-question-card">
              <div className="row-between quiz-question-header">
                <div className="section-title quiz-question-title">
                  Frage {qIdx + 1}
                </div>
                <button
                  className="btn btn-danger quiz-remove-btn"
                  onClick={() => removeQuestion(qIdx)}
                  disabled={questions.length === 1}
                >
                  Entfernen
                </button>
              </div>

              <div className="form-group">
                <label>Frage</label>
                <textarea
                  className="form-control" rows={2}
                  value={q.question}
                  onChange={e => updateQuestion(qIdx, 'question', e.target.value)}
                  placeholder="Frage eingeben..."
                />
              </div>

              <div className="form-group">
                <label>Antwortmöglichkeiten — klicken Sie auf ✓ um die richtige Antwort zu markieren</label>
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} className="option-row">
                    <button
                      className={`option-correct-btn ${q.correct === oIdx ? 'selected' : ''}`}
                      onClick={() => updateQuestion(qIdx, 'correct', oIdx)}
                      title="Als richtige Antwort markieren"
                    >
                      {q.correct === oIdx ? '✓' : '○'}
                    </button>
                    <input
                      className="form-control quiz-option-input" type="text"
                      value={opt}
                      onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                      placeholder={`Antwort ${oIdx + 1}...`}
                    />
                  </div>
                ))}
              </div>

              <div className="quiz-correct-hint">
                Richtige Antwort: Antwort {q.correct + 1}
                {q.options[q.correct] ? ` — "${q.options[q.correct]}"` : ''}
              </div>
            </div>
          ))}

          <button className="btn btn-ghost" onClick={addQuestion}>+ Frage hinzufügen</button>

          <div className="quiz-save-row">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner spinner-sm" /> Speichern...</> : 'Speichern'}
            </button>
          </div>
        </>
      )}

      {selectedTenant && (
        <VersionHistoryPanel table="quiz_questions" tenantId={selectedTenant} onRestored={loadQuestions} />
      )}
    </div>
  )
}

export default QuizEditor
